import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetFeatureLazyQuery as _useGetFeatureLazyQuery, useSetTscliCommandMutation as _useSetTscliCommandMutation, } from '../../../../services/generated/graphql-types';
const toGroupConfig = (data) => {
    if (!data.groupName)
        return null;
    return {
        groupName: data.groupName,
        settings: (data.settings ?? []).map(s => s
            ? {
                settingName: s.settingName ?? '',
                resetToDefault: s.resetToDefault ?? undefined,
                configs: (s.configs ?? []).map(c => c
                    ? {
                        configName: c.configName ?? '',
                        elementType: c.elementType ?? '',
                        orgAware: c.orgAware ?? undefined,
                        infoTooltip: c.infoTooltip ?? undefined,
                        value: c.value ?? undefined,
                        elementConfig: c.elementConfig
                            ? {
                                type: c.elementConfig.type ??
                                    undefined,
                                groups: (c.elementConfig.groups ?? []).map(g => ({
                                    options: (g?.options ?? []).filter((o) => o !== null),
                                })),
                            }
                            : undefined,
                        validation: c.validation
                            ? {
                                required: c.validation.required ??
                                    undefined,
                                regex: c.validation.regex ??
                                    undefined,
                                maxLength: c.validation.maxLength ??
                                    undefined,
                                minLength: c.validation.minLength ??
                                    undefined,
                                isNumeric: c.validation.isNumeric ??
                                    undefined,
                                range: c.validation.range ??
                                    undefined,
                            }
                            : undefined,
                    }
                    : null),
            }
            : null),
    };
};
export const useTscliGroup = (groupName) => {
    const [isSaving, setIsSaving] = useState(false);
    const [lastError, setLastError] = useState(null);
    const [isBackgroundFetching, setIsBackgroundFetching] = useState(false);
    const hasLoadedOnce = useRef(false);
    const payloadRef = useRef(null);
    const [_getFeature, { data: rawFeatureData, loading: apolloFetching },] = _useGetFeatureLazyQuery({
        fetchPolicy: 'no-cache',
    });
    const featureData = rawFeatureData?.Admin_getFeature?.Data;
    if (featureData && !hasLoadedOnce.current) {
        hasLoadedOnce.current = true;
    }
    const groupConfig = featureData ? toGroupConfig(featureData) : null;
    useEffect(() => {
        if (featureData) {
            setIsBackgroundFetching(false);
        }
    }, [featureData]);
    const [mutate] = _useSetTscliCommandMutation();
    const fireMutation = useCallback((featureName, params, viewMode, callbacks) => {
        setIsSaving(true);
        setLastError(null);
        payloadRef.current = { featureName, params, viewMode, callbacks };
        mutate({ variables: { featureName, params, viewMode } })
            .then(() => {
            setIsSaving(false);
            callbacks?.onMutationSuccess?.();
            setIsBackgroundFetching(true);
            return _getFeature({
                variables: { featureName: groupName, viewMode },
            });
        })
            .then(result => {
            const data = result?.data?.Admin_getFeature?.Data;
            const refreshed = data ? toGroupConfig(data) : null;
            return callbacks?.onSuccess?.(refreshed);
        })
            .catch((err) => {
            setIsSaving(false);
            setIsBackgroundFetching(false);
            setLastError(err);
            callbacks?.onError?.(err);
        });
    }, [mutate, groupName, _getFeature]);
    const fetch = useCallback((viewMode) => {
        _getFeature({
            variables: { featureName: groupName, viewMode },
        });
    }, [_getFeature, groupName]);
    const save = useCallback((rawOptions, viewMode, callbacks) => {
        const configOptions = rawOptions.map(opt => ({
            optionKey: opt.optionKey,
            optionValue: btoa(opt.optionValue),
            ...(opt.optionLabel !== undefined && {
                optionLabel: opt.optionLabel,
            }),
        }));
        fireMutation(groupName, { configOptions, configOperation: 'add' }, viewMode, callbacks);
    }, [groupName, fireMutation]);
    const retry = useCallback(() => {
        if (!payloadRef.current)
            return;
        const { featureName, params, viewMode, callbacks } = payloadRef.current;
        fireMutation(featureName, params, viewMode, callbacks);
    }, [fireMutation]);
    const reset = useCallback((configKeys, viewMode, callbacks) => {
        const configOptions = configKeys.map(key => ({
            optionKey: key,
            optionValue: '',
        }));
        fireMutation(groupName, { configOperation: 'reset', configOptions }, viewMode, callbacks);
    }, [groupName, fireMutation]);
    return {
        groupConfig,
        isFetching: !!apolloFetching && !hasLoadedOnce.current,
        isBackgroundFetching,
        isSaving,
        fetch,
        save,
        retry,
        reset,
        lastError,
    };
};
//# sourceMappingURL=use-tscli-group.js.map